import { Injectable, inject } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Observable, throwError } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { Icon } from '../models/icon.model';
import { ProductV2 } from '../models/product-v2.model';
import { CategoryIconMapperService } from '../../shared/services/category-icon-mapper.service';
import { AvailableOffersFacade } from '../store/available-offers/available-offers.facade';
import { applyTelemedicineVariationFilter } from '../utils/telemedicine-product-variations.util';

import { Ga4Item } from './gtm.service';
import { ProductV2Service } from './product-v2.service';
import { ChannelEventsService } from './channel-events.service';
import { ProductPageMindDetailService } from './product-page-mind-detail.service';


/** Resultado do carregamento de produto para a página de detalhe (SVA ou MIND). */
export interface ProductPageProductLoadResult {
  product: ProductV2;
  sanitizedDescription: SafeHtml;
  currentIcon: Icon;
  gaItem: Ga4Item;
  /** Fluxo MIND: atualizar `currentCategory` da página com o título do grupo. */
  resolvedCategory?: string;
}

/**
 * Carrega produto para a página de detalhe — API SVA (`productsv2`) ou catálogo MIND.
 * Complementa {@link ProductPageMindDetailService} com apresentação (HTML, ícone, GA item).
 */
@Injectable({ providedIn: 'root' })
export class ProductPageProductLoadService {
  private productV2Service = inject(ProductV2Service);
  private sanitizer = inject(DomSanitizer);
  private categoryIconMapper = inject(CategoryIconMapperService);
  private availableOffersFacade = inject(AvailableOffersFacade);
  private channelEventsService = inject(ChannelEventsService);
  private mindDetailService = inject(ProductPageMindDetailService);

  loadSvaByUuid(
    uuid: string,
    categoryForAnalytics: string
  ): Observable<ProductPageProductLoadResult> {
    return this.productV2Service.getProductByUuid(uuid).pipe(
      map(resp => resp.data),
      tap(product => {
        applyTelemedicineVariationFilter(product);
        if (product) {
          this.availableOffersFacade.mapProductVariationsToOffers(product);
          this.channelEventsService
            .sendProductViewedEvent(product.name, {
              ...(product.uuid ? { productUuid: product.uuid } : {}),
            })
            .subscribe();
        }
      }),
      map(product => this.buildPresentation(product, categoryForAnalytics))
    );
  }

  loadMindCatalogDetail(
    productUuidStr: string,
    currentCategory: string
  ): Observable<ProductPageProductLoadResult> {
    const productId = Number(productUuidStr);
    if (!Number.isFinite(productId) || productId <= 0) {
      return throwError(() => new Error('MIND_NOT_FOUND'));
    }

    return this.mindDetailService
      .loadByCatalogProductId(productId, currentCategory, productUuidStr)
      .pipe(
        tap(({ product }) => {
          this.availableOffersFacade.mapProductVariationsToOffers(product);
          this.channelEventsService
            .sendProductViewedEvent(product.name, {
              ...(product.uuid ? { productUuid: product.uuid } : {}),
            })
            .subscribe();
        }),
        map(({ product, categoryAfterResolve }) =>
          this.buildPresentation(
            product,
            categoryAfterResolve,
            categoryAfterResolve
          )
        )
      );
  }

  private buildPresentation(
    product: ProductV2,
    gaItemCategory: string,
    resolvedCategory?: string
  ): ProductPageProductLoadResult {
    const sanitizedDescription = this.sanitizer.bypassSecurityTrustHtml(
      product.excerpt
    ) as SafeHtml;

    const currentIcon = this.categoryIconMapper.mapProductToIcon(
      product.categories || [],
      product.productType
    );

    const gaItem: Ga4Item = {
      item_id: product.sku || product.uuid || product.id,
      item_name: product.name,
      item_category: gaItemCategory,
      price: product.salesPrice ?? product.price ?? 0,
    };

    return {
      product,
      sanitizedDescription,
      currentIcon,
      gaItem,
      resolvedCategory,
    };
  }
}
